import { useState } from 'react'
import { Button, TextField } from '@krp/ui-kit'
import { codeProblem } from '../model/profile'

export interface OwnCashierCodeCardProps {
  hasCode: boolean
  codeMinLength: number
  codeMaxLength: number
  canManage: boolean
  busy: boolean
  /** Задать код; код в состояние приложения не попадает и стирается из поля после отправки. */
  submit: (code: string) => Promise<boolean>
  /** До какого времени (ISO) заперт вход по коду после неверных кодов; `null` — не заперт (истёкшие блокировки сервер не отдаёт). */
  lockedUntil: string | null
  /** Снять блокировку входа (К-121); код не меняется. */
  unlock: () => Promise<boolean>
}

const timeOf = (iso: string) => new Date(iso).toLocaleString('ru-RU', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })

/** Код входа кассира в РМК: произвольный секрет (PIN, буквы-цифры, токен, штрихкод), одним полем, без повтора. Сам код нигде не показывается. */
export function OwnCashierCodeCard(props: OwnCashierCodeCardProps) {
  const [code, setCode] = useState('')
  const problem = codeProblem(code, props.codeMinLength, props.codeMaxLength)
  const ready = props.canManage && !props.busy && problem === null
  const send = async () => {
    if (!ready) return
    await props.submit(code)
    setCode('')
  }
  return <section className="own-cashiers__card" aria-label="Код кассира">
    <h3>Код кассира</h3>
    <p role="status">{props.hasCode
      ? '🔒 Задан личный код. По уникальному коду или штрихкоду кассир входит, не выбирая себя из списка. Новый код заменит прежний — кассиру придётся войти в РМК заново.'
      : 'ℹ Личного кода нет — действует код по умолчанию «1111». По нему кассир входит, выбрав себя из списка. Можно задать свой код (любой набор от 4 знаков).'}</p>
    {props.lockedUntil !== null && <div role="group" aria-label="Блокировка входа">
      <p role="status">⛔ Вход по коду заблокирован до {timeOf(props.lockedUntil)}: для этого кассира вводили неверные коды. На кассе, где их вводили, войти нельзя до этого времени.</p>
      <Button actionId="receipts.own-cashiers.unlock-sign-in" busy={props.busy} disabled={!props.canManage || props.busy}
        disabledReason={props.canManage ? null : 'Нужно право управления кассирами'} onClick={() => { void props.unlock() }}>Снять блокировку входа</Button>
    </div>}
    <fieldset disabled={!props.canManage || props.busy}>
      <TextField label={props.hasCode ? 'Новый код' : 'Код'} type="password" value={code} onChange={setCode} />
      {code !== '' && problem !== null && <p role="status">⚠ {problem}</p>}
      <div className="own-cashiers__actions">
        <Button variant="primary" actionId="receipts.own-cashiers.set-code" busy={props.busy} disabled={!ready}
          disabledReason={props.canManage ? problem : 'Нужно право управления кассирами'} onClick={() => { void send() }}>
          {props.hasCode ? 'Сменить код' : 'Задать код'}</Button>
      </div>
    </fieldset>
  </section>
}
